import React, { useState, useEffect, useCallback } from 'react';
import { 
  ArrowUpRight, ArrowDownRight, Clock, 
  CheckCircle2, XCircle, MessageSquare, RefreshCw,
  TrendingUp, Handshake, AlertTriangle, ChevronRight
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import api from '@/services/api';
import AEDIcon from '@/components/icons/AEDIcon';

// Offer state config
const OFFER_STATE_CONFIG = {
  submitted: { label: 'Pending', color: 'bg-blue-600/20 text-blue-400 border-blue-600/40', icon: Clock },
  countered: { label: 'Countered', color: 'bg-gold-600/20 text-gold-400 border-gold-600/40', icon: MessageSquare },
  accepted: { label: 'Accepted', color: 'bg-green-600/20 text-green-400 border-green-600/40', icon: CheckCircle2 },
  rejected: { label: 'Rejected', color: 'bg-red-600/20 text-red-400 border-red-600/40', icon: XCircle },
  withdrawn: { label: 'Withdrawn', color: 'bg-gray-600/20 text-gray-400 border-gray-600/40', icon: XCircle },
  expired: { label: 'Expired', color: 'bg-gray-600/20 text-gray-400 border-gray-600/40', icon: Clock }
};

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-AE', { 
    style: 'currency', 
    currency: 'AED',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-AE', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Single Offer Card
const OfferCard = ({ offer, userRole, userId, onAction, isLoading }) => {
  const stateConfig = OFFER_STATE_CONFIG[offer.state] || OFFER_STATE_CONFIG.submitted;
  const StateIcon = stateConfig.icon;
  
  const isBuyer = userId === offer.buyer_id;
  const isSeller = userId === offer.seller_id;
  const canRespond = (offer.state === 'submitted' || offer.state === 'countered') && !isLoading;
  
  // Determine who needs to act
  const lastAction = offer.negotiation_history?.[offer.negotiation_history.length - 1];
  const waitingForSeller = lastAction?.party === 'buyer' && offer.state !== 'accepted';
  const waitingForBuyer = lastAction?.party === 'seller' && offer.state !== 'accepted';
  
  return (
    <div className="bg-white/5 border border-white/10 rounded-lg p-4 hover:bg-white/8 transition-colors">
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <Badge variant="outline" className={stateConfig.color}>
            <StateIcon className="w-3 h-3 mr-1" />
            {stateConfig.label}
          </Badge>
          {offer.counter_count > 0 && (
            <Badge variant="outline" className="bg-white/5 text-gray-400 border-white/10">
              {offer.counter_count} rounds
            </Badge>
          )}
        </div>
        {offer.deal_id && (
          <Badge className="bg-green-600/20 text-green-400">
            Deal Created
          </Badge>
        )}
      </div>
      
      {/* Amounts */}
      <div className="grid grid-cols-3 gap-4 mb-4">
        <div>
          <div className="text-xs text-gray-500 mb-1">Current Offer</div>
          <div className="text-lg font-bold text-white">{formatCurrency(offer.current_amount)}</div>
        </div>
        <div>
          <div className="text-xs text-gray-500 mb-1">Asking Price</div>
          <div className="text-lg font-medium text-gray-300">{formatCurrency(offer.asking_price)}</div>
        </div>
        <div>
          <div className="text-xs text-gray-500 mb-1">Difference</div>
          <div className={`text-lg font-medium flex items-center ${offer.offer_vs_asking_pct >= 0 ? 'text-green-400' : 'text-red-400'}`}>
            {offer.offer_vs_asking_pct >= 0 ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
            {Math.abs(offer.offer_vs_asking_pct).toFixed(1)}%
          </div>
        </div>
      </div>
      
      {/* Waiting indicator */}
      {canRespond && (
        <div className={`text-sm mb-3 p-2 rounded ${
          (isSeller && waitingForSeller) || (isBuyer && waitingForBuyer)
            ? 'bg-amber-900/20 text-amber-400 border border-amber-600/20'
            : 'bg-white/5 text-gray-400'
        }`}>
          {waitingForSeller && (isSeller ? "⏳ Your turn to respond" : "Waiting for seller response")}
          {waitingForBuyer && (isBuyer ? "⏳ Your turn to respond" : "Waiting for buyer response")}
        </div>
      )}
      
      {/* Timeline */}
      {offer.negotiation_history && offer.negotiation_history.length > 0 && (
        <div className="mb-4">
          <div className="text-xs text-gray-500 mb-2">Negotiation History</div>
          <div className="space-y-1">
            {offer.negotiation_history.slice(-3).map((entry, idx) => (
              <div key={idx} className="flex items-center text-xs text-gray-400">
                <span className={`w-16 font-medium ${entry.party === 'buyer' ? 'text-blue-400' : 'text-amber-400'}`}>
                  {entry.party}
                </span>
                <span className="flex-1">{entry.action}</span>
                {entry.amount && <span className="text-white">{formatCurrency(entry.amount)}</span>}
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Actions */}
      {canRespond && (
        <div className="flex gap-2 pt-3 border-t border-white/10">
          {isSeller && (
            <>
              <Button 
                size="sm" 
                className="flex-1 bg-green-600 hover:bg-green-700"
                onClick={() => onAction('accept', offer.offer_id)}
                disabled={isLoading}
              >
                <CheckCircle2 className="w-4 h-4 mr-1" />
                Accept
              </Button>
              <Button 
                size="sm" 
                variant="outline"
                className="flex-1 bg-gold-600/20 text-gold-400 border-gold-600/30 hover:bg-gold-600/30"
                onClick={() => onAction('counter', offer.offer_id)}
                disabled={isLoading}
              >
                <MessageSquare className="w-4 h-4 mr-1" />
                Counter
              </Button>
              <Button 
                size="sm" 
                variant="outline"
                className="bg-red-600/20 text-red-400 border-red-600/30 hover:bg-red-600/30"
                onClick={() => onAction('reject', offer.offer_id)}
                disabled={isLoading}
              >
                <XCircle className="w-4 h-4 mr-1" />
                Reject
              </Button>
            </>
          )}
          {isBuyer && waitingForBuyer && (
            <>
              <Button 
                size="sm" 
                className="flex-1 bg-green-600 hover:bg-green-700"
                onClick={() => onAction('accept', offer.offer_id)}
                disabled={isLoading}
              >
                <CheckCircle2 className="w-4 h-4 mr-1" />
                Accept Counter
              </Button>
              <Button 
                size="sm" 
                variant="outline"
                className="flex-1 bg-gold-600/20 text-gold-400 border-gold-600/30 hover:bg-gold-600/30"
                onClick={() => onAction('counter', offer.offer_id)}
                disabled={isLoading}
              >
                <MessageSquare className="w-4 h-4 mr-1" />
                Counter
              </Button>
            </>
          )}
          {isBuyer && !waitingForBuyer && (
            <Button 
              size="sm" 
              variant="outline"
              className="bg-gray-600/20 text-gray-400 border-gray-600/30 hover:bg-gray-600/30"
              onClick={() => onAction('withdraw', offer.offer_id)}
              disabled={isLoading}
            >
              Withdraw
            </Button>
          )}
        </div>
      )}
      
      {/* Expiry */}
      {offer.valid_until && offer.state !== 'accepted' && offer.state !== 'rejected' && (
        <div className="mt-3 text-xs text-gray-500">
          Valid until: {formatDate(offer.valid_until)}
        </div>
      )}
    </div>
  );
};

// Counter Dialog
const CounterDialog = ({ open, onClose, onSubmit, currentAmount, isLoading }) => {
  const [counterAmount, setCounterAmount] = useState('');
  const [message, setMessage] = useState('');
  
  const handleSubmit = () => {
    if (!counterAmount || parseFloat(counterAmount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    onSubmit(parseFloat(counterAmount), message);
  };
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#1a1a2e] border-white/10">
        <DialogHeader>
          <DialogTitle className="text-white">Counter Offer</DialogTitle>
          <DialogDescription className="text-gray-400">
            Current offer: {formatCurrency(currentAmount)}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div>
            <label className="text-sm text-gray-400 mb-2 block">Counter Amount (AED)</label>
            <Input
              type="number"
              placeholder="Enter amount"
              value={counterAmount}
              onChange={(e) => setCounterAmount(e.target.value)}
              className="bg-white/5 border-white/10"
            />
          </div>
          <div>
            <label className="text-sm text-gray-400 mb-2 block">Message (optional)</label>
            <Textarea
              placeholder="Add a message..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="bg-white/5 border-white/10"
              rows={3}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="bg-white/5 border-white/10">
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={isLoading}
            className="bg-gold-600 hover:bg-gold-700"
          >
            Send Counter Offer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Main Offers Panel Component
const OffersPanel = ({ propertyId = null, sellerId = null, buyerId = null, compact = false, isAdmin = false }) => {
  const [offers, setOffers] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [counterDialog, setCounterDialog] = useState({ open: false, offerId: null, currentAmount: 0 });
  
  // Get current user (simplified - in production use auth context)
  const currentUserId = sellerId || buyerId;
  const userRole = sellerId ? 'seller' : (isAdmin ? 'admin' : 'buyer');

  const fetchOffers = useCallback(async () => {
    setLoading(true);
    try {
      let offersRes;
      
      // Admin mode: fetch ALL platform offers
      if (isAdmin || (!propertyId && !sellerId && !buyerId)) {
        offersRes = await api.get('/v2/offers/all?include_terminal=true&limit=100');
      } else if (propertyId) {
        offersRes = await api.get(`/v2/offers/property/${propertyId}?include_terminal=true`);
      } else if (sellerId) {
        offersRes = await api.get(`/v2/offers/seller/${sellerId}?include_terminal=true`);
      } else if (buyerId) {
        offersRes = await api.get(`/v2/offers/buyer/${buyerId}?include_terminal=true`);
      }
      
      setOffers(offersRes?.data?.offers || []);
      
      // Get statistics
      const statsRes = await api.get('/v2/offers/statistics/summary');
      setStatistics(statsRes.data);
    } catch (error) {
      console.error('Failed to fetch offers:', error);
      toast.error('Failed to load offers');
    } finally {
      setLoading(false);
    }
  }, [propertyId, sellerId, buyerId, isAdmin]);

  useEffect(() => {
    fetchOffers();
  }, [fetchOffers]);

  const handleAction = async (action, offerId) => {
    if (action === 'counter') {
      const offer = offers.find(o => o.offer_id === offerId);
      setCounterDialog({ open: true, offerId, currentAmount: offer?.current_amount || 0 });
      return;
    }
    
    setActionLoading(true);
    try {
      await api.post(`/v2/offers/${offerId}/${action}`, {
        user_id: currentUserId,
        message: `${action} from UI`
      });
      toast.success(`Offer ${action}ed successfully`);
      fetchOffers();
    } catch (error) {
      console.error(`Failed to ${action} offer:`, error);
      toast.error(error.response?.data?.detail || `Failed to ${action} offer`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleCounter = async (counterAmount, message) => {
    setActionLoading(true);
    try {
      await api.post(`/v2/offers/${counterDialog.offerId}/counter`, {
        counter_amount: counterAmount,
        message,
        user_id: currentUserId
      });
      toast.success('Counter offer sent');
      setCounterDialog({ open: false, offerId: null, currentAmount: 0 });
      fetchOffers();
    } catch (error) {
      console.error('Failed to send counter offer:', error);
      toast.error(error.response?.data?.detail || 'Failed to send counter offer');
    } finally {
      setActionLoading(false);
    }
  };

  // Compact view
  if (compact) {
    const activeOffers = offers.filter(o => o.state === 'submitted' || o.state === 'countered');
    
    return (
      <Card className="bg-[#1a1a2e] border-white/10 h-full">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2 text-white">
              <AEDIcon className="w-4 h-4 text-green-400" />
              Offers
            </CardTitle>
            <Badge className="bg-blue-600/20 text-blue-400">
              {activeOffers.length} active
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          {activeOffers.length === 0 ? (
            <div className="text-center py-6 text-gray-400 text-sm">
              No active offers
            </div>
          ) : (
            <div className="space-y-2">
              {activeOffers.slice(0, 3).map((offer) => (
                <div 
                  key={offer.offer_id}
                  className="p-2 bg-white/5 rounded flex items-center justify-between"
                >
                  <div>
                    <div className="text-white font-medium">{formatCurrency(offer.current_amount)}</div>
                    <div className="text-xs text-gray-500">{offer.counter_count} rounds</div>
                  </div>
                  <Badge variant="outline" className={OFFER_STATE_CONFIG[offer.state]?.color}>
                    {OFFER_STATE_CONFIG[offer.state]?.label}
                  </Badge>
                </div>
              ))}
            </div>
          )}
          
          {statistics && (
            <div className="mt-3 pt-3 border-t border-white/10 text-center">
              <div className="text-2xl font-bold text-green-400">{statistics.conversion_rate}%</div>
              <div className="text-xs text-gray-500">Conversion Rate</div>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  // Full view
  return (
    <>
      <Card className="bg-[#1a1a2e] border-white/10">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2 text-white">
              <AEDIcon className="w-5 h-5 text-green-400" />
              Offers Management
            </CardTitle>
            <Button 
              size="sm" 
              variant="ghost" 
              onClick={fetchOffers}
              className="text-gray-400"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Statistics */}
          {statistics && (
            <div className="grid grid-cols-5 gap-2 mb-4">
              <div className="text-center p-2 bg-white/5 rounded">
                <div className="text-lg font-bold text-white">{statistics.total_offers}</div>
                <div className="text-xs text-gray-500">Total</div>
              </div>
              <div className="text-center p-2 bg-blue-900/20 rounded">
                <div className="text-lg font-bold text-blue-400">{statistics.pending}</div>
                <div className="text-xs text-gray-500">Pending</div>
              </div>
              <div className="text-center p-2 bg-green-900/20 rounded">
                <div className="text-lg font-bold text-green-400">{statistics.accepted}</div>
                <div className="text-xs text-gray-500">Accepted</div>
              </div>
              <div className="text-center p-2 bg-red-900/20 rounded">
                <div className="text-lg font-bold text-red-400">{statistics.rejected}</div>
                <div className="text-xs text-gray-500">Rejected</div>
              </div>
              <div className="text-center p-2 bg-green-900/20 rounded">
                <div className="text-lg font-bold text-green-400">{statistics.conversion_rate}%</div>
                <div className="text-xs text-gray-500">Conversion</div>
              </div>
            </div>
          )}
          
          <Separator className="bg-white/10 my-4" />
          
          {/* Offers List */}
          <ScrollArea className="h-[500px]">
            {offers.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <AEDIcon className="w-16 h-16 mx-auto mb-4 opacity-30" />
                <p className="text-lg">No offers yet</p>
                <p className="text-sm mt-2">Offers will appear here when buyers submit them</p>
              </div>
            ) : (
              <div className="space-y-4 pr-4">
                {offers.map((offer) => (
                  <OfferCard 
                    key={offer.offer_id}
                    offer={offer}
                    userRole={userRole}
                    userId={currentUserId}
                    onAction={handleAction}
                    isLoading={actionLoading}
                  />
                ))}
              </div>
            )}
          </ScrollArea>
        </CardContent>
      </Card>
      
      <CounterDialog
        open={counterDialog.open}
        onClose={() => setCounterDialog({ open: false, offerId: null, currentAmount: 0 })}
        onSubmit={handleCounter}
        currentAmount={counterDialog.currentAmount}
        isLoading={actionLoading}
      />
    </>
  );
};

export default OffersPanel;
